import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CircleAlert, Loader2 } from 'lucide-react';

import { authApi } from '@/features/auth/auth.api';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth.store';
import { useT } from '@/i18n';
import { resolveLoginRedirect } from '@/lib/auth-redirect';
import { LangToggle } from '@/components/chassis/LangToggle';

/**
 * Entrada al sistema — design spec «Login» del CEO (Claude Design, 2026-08-20).
 *
 * La pantalla es UNA para los cinco roles (admin, supervisor, agente, uploader,
 * navigator): quien entra por acá cae en su módulo según su rol, así que el
 * rediseño alcanza a todos con una sola implementación.
 *
 * El ensamble del spec: panel de marca navy a la izquierda (460px) y la columna
 * de trabajo a la derecha — sólo ésa cambia entre estados. El panel de marca
 * nunca se mueve. Un único botón primario por estado. La nota de HIPAA al pie,
 * presente y sin alarmar.
 *
 * DESVIACIONES DECLARADAS (ninguna inventada; todas por lo que hoy da el
 * backend, y todas en el checkpoint):
 *  · el lockup blanco del panel se compone con el mark + el wordmark en
 *    Montserrat — el mismo tratamiento que `ScrBrandRow` usa en todo el módulo
 *    Navigation. El PNG del lockup queda pendiente de subirlo al repo.
 *  · «Quedan N intentos antes de que se bloquee»: el servidor no devuelve los
 *    intentos restantes, así que se muestra su mensaje tal cual.
 *  · «Se desbloquea a las 8:52 am»: el servidor tampoco devuelve `lockedUntil`
 *    en la respuesta del login, así que el bloqueo se dibuja sin la hora.
 *  · «Contraseña nueva / primer ingreso»: no existe `must_change_password` en
 *    el modelo. Sin superficie hasta que el CEO lo decida.
 *
 * F5-T7 (contrato Agente-05, 2026-08-28):
 *  · MUERE «Recordar este equipo» — la casilla no hacía nada, y lo que
 *    prometía es lo que no queremos en máquinas compartidas de call center.
 *  · «Olvidé mi contraseña» es TEXTO («pedile el reseteo a un admin») — el
 *    flow real espera la firma F-13 (proveedor de email) y es backend nuevo.
 *  · Cuenta bloqueada: el primario queda INERTE con la razón en su propio
 *    label (desaparecer haría pensar que la pantalla se rompió); el server no
 *    devuelve el tiempo restante, así que el label no inventa minutos. Editar
 *    los campos re-arma el intento.
 *  · El toggle de idioma SÍ está en el login («alguien que no entiende la
 *    pantalla no puede pedir ayuda») — arriba de la columna de trabajo,
 *    jamás un segundo primario.
 */

interface LoginFormData {
  email: string;
  password: string;
  mfaCode: string;
}

/** Tokens del brand (los mismos del módulo Navigation), en clases compuestas. */
const INP =
  'w-full box-border font-body text-[15px] text-nav-navy bg-white border-[1.5px] border-nav-line '
  + 'rounded-lg px-3.5 py-3 focus:outline focus:outline-2 focus:outline-nav-teal focus:outline-offset-1 '
  + 'focus:border-nav-teal placeholder:text-nav-faint';
const FLABEL =
  'block font-head text-[11px] font-bold tracking-[.08em] uppercase text-nav-slate mb-[7px]';
const BTN_PRI =
  'inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-transparent '
  + 'bg-nav-navy text-white font-head font-semibold cursor-pointer hover:bg-nav-navyHover '
  + 'disabled:opacity-45 disabled:cursor-not-allowed transition-colors';
const BTN_SEC =
  'inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-nav-line '
  + 'bg-white text-nav-navy font-head font-semibold cursor-pointer hover:border-nav-teal '
  + 'hover:text-nav-tealDark disabled:opacity-45 transition-colors';
const GHO =
  'bg-transparent border-0 text-nav-teal font-head font-semibold text-[13px] py-1.5 cursor-pointer '
  + 'hover:text-nav-tealDark transition-colors';
const KICK = 'font-head font-bold text-[10px] tracking-[.14em] uppercase text-nav-teal m-0 mb-2.5';

export function LoginPage(): JSX.Element {
  const t = useT();
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [mfaPending, setMfaPending] = useState(false);
  const [enrollmentRequired, setEnrollmentRequired] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setFocus,
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', mfaCode: '' },
  });

  const code = watch('mfaCode') ?? '';
  // F5-T7: con la cuenta bloqueada el primario queda inerte; editar cualquier
  // campo re-arma el intento (el desbloqueo real lo decide el server).
  const email = watch('email');
  const password = watch('password');
  useEffect(() => {
    if (locked) setLocked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  const onSubmit = async (data: LoginFormData): Promise<void> => {
    setServerError(null);
    setEnrollmentRequired(false);
    setLocked(false);
    setSubmitting(true);
    try {
      const tokens = await authApi.login({
        email: data.email,
        password: data.password,
        mfaCode: data.mfaCode || undefined,
      });
      setSession(tokens);
      const target = resolveLoginRedirect(tokens.user.role, from);
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isMfaRequired) {
          setMfaPending(true);
          setTimeout(() => setFocus('mfaCode'), 50);
          setServerError(null);
        } else if (err.isMfaEnrollmentRequired) {
          setEnrollmentRequired(true);
        } else {
          // El bloqueo es un ESTADO propio del spec, no un error más: el
          // servidor lo manda como 403 y su mensaje lo nombra.
          if (err.status === 403 && /lock/i.test(err.message)) setLocked(true);
          setServerError(err.message);
        }
      } else {
        setServerError(t('login.offline'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-nav-bg font-body text-nav-navy flex items-center justify-center p-4 sm:p-8">
      <div
        className="w-full max-w-[1180px] bg-white rounded-[14px] overflow-hidden
                   shadow-[0_1px_2px_rgba(18,43,68,.06),0_8px_24px_rgba(18,43,68,.10)]
                   grid grid-cols-1 md:grid-cols-[460px_1fr] md:min-h-[660px]"
      >
        {/* ------------------------------------------- el panel de marca
            Sostiene el logo y UNA frase. No cambia entre estados. */}
        <div className="bg-[#12273d] text-white px-8 py-10 sm:px-11 sm:py-11 flex flex-col justify-between gap-10">
          {/* El lockup del mockup, en blanco. Sale del kit de marca del CEO
              (Recurso 2), al que se le quito el fondo navy para que sirva
              sobre cualquier navy y no solo sobre el exacto del panel. */}
          <img
            src="/lockup-white.png"
            alt="Pathway to Better Days Inc."
            className="w-[158px] h-auto"
          />
          <div>
            <p className="font-head text-[26px] sm:text-[30px] font-bold leading-[1.15] tracking-[-0.02em] m-0 mb-3.5">
              {t('login.brandLine')}
            </p>
            <p className="text-[14.5px] leading-[1.55] text-[#a9bccd] m-0">{t('login.brandSub')}</p>
          </div>
          <p className="text-xs text-[#7e97ad] m-0">{t('login.brandFoot')}</p>
        </div>

        {/* --------------------------------------- la columna de trabajo */}
        <div className="px-7 py-10 sm:px-[60px] sm:py-14 flex flex-col justify-center">
          <div className="w-full max-w-[400px]">
            {/* F5-T7: el toggle de idioma vive en el login — quien no
                entiende la pantalla no puede pedir ayuda. */}
            <div className="flex justify-end mb-5">
              <LangToggle />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {!mfaPending ? (
                <>
                  <p className={KICK}>{t('login.kicker')}</p>
                  <h1 className="font-head text-[28px] font-extrabold leading-[1.1] m-0 mb-2">
                    {t('login.title')}
                  </h1>
                  <p className="text-[14.5px] text-nav-slate m-0 mb-[30px]">{t('login.mfaMaybe')}</p>

                  <label className={FLABEL} htmlFor="lg-mail">{t('login.email')}</label>
                  <input
                    id="lg-mail"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    className={INP}
                    placeholder="nombre@pathwaybetterdays.com"
                    aria-invalid={errors.email ? true : undefined}
                    {...register('email', { required: t('login.emailRequired') })}
                  />
                  {errors.email ? (
                    <p className="text-[12.5px] text-nav-red m-0 mt-1.5 mb-4" role="alert">
                      {errors.email.message}
                    </p>
                  ) : (
                    <div className="mb-5" />
                  )}

                  <label className={FLABEL} htmlFor="lg-pass">{t('login.password')}</label>
                  <div className="relative">
                    <input
                      id="lg-pass"
                      type={showPass ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`${INP} pr-[76px]`}
                      aria-invalid={errors.password ? true : undefined}
                      {...register('password', { required: t('login.passwordRequired') })}
                    />
                    <button
                      type="button"
                      className={`${GHO} absolute right-3.5 top-2.5 text-xs`}
                      onClick={() => setShowPass((v) => !v)}
                    >
                      {showPass ? t('login.hide') : t('login.show')}
                    </button>
                  </div>
                  {errors.password ? (
                    <p className="text-[12.5px] text-nav-red m-0 mt-1.5" role="alert">
                      {errors.password.message}
                    </p>
                  ) : null}

                  {/* F5-T7: «Recordar este equipo» NO se dibuja (muere). El
                      reseteo de contraseña espera F-13 — texto, sin flow. */}
                  <p className="text-[13px] text-nav-slate m-0 mt-3 mb-[26px]">
                    {t('agentF5.lg.forgotText')}
                  </p>

                  {/* El bloqueo tiene su propia caja: qué pasó, que no se
                      perdió nada, y cuál es el único paso siguiente. */}
                  {locked ? (
                    <div className="bg-nav-amberTint rounded-[9px] px-3.5 py-3 mb-5" role="alert">
                      <p className="font-head text-[15px] font-bold text-nav-amberText m-0 mb-1">
                        {t('login.lockedTitle')}
                      </p>
                      <p className="text-[13.5px] text-nav-amberText m-0">{t('login.lockedBody')}</p>
                    </div>
                  ) : serverError ? (
                    <div className="flex gap-2.5 items-start bg-nav-redTint rounded-[9px] px-3.5 py-3 mb-5" role="alert">
                      <CircleAlert className="h-[18px] w-[18px] flex-none mt-0.5 text-nav-red" aria-hidden />
                      <p className="text-[13.5px] text-nav-red m-0">{serverError}</p>
                    </div>
                  ) : null}

                  {enrollmentRequired && (
                    <div className="bg-nav-tealTint rounded-[9px] px-3.5 py-3 mb-5" role="alert">
                      <p className="text-[13.5px] text-nav-tealDark m-0">{t('login.mfaEnroll')}</p>
                    </div>
                  )}

                  {/* F5-T7: bloqueada ⇒ primario INERTE con la razón en su
                      propio label — no desaparece. */}
                  <button
                    type="submit"
                    disabled={submitting || locked}
                    className={`${BTN_PRI} w-full py-3.5 text-base`}
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                    {locked
                      ? t('agentF5.lg.lockedBtn')
                      : submitting
                        ? t('login.entering')
                        : t('login.button')}
                  </button>
                  {submitting && (
                    <p className="text-[13px] text-nav-slate m-0 mt-3 text-center">{t('login.dontClose')}</p>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={`${GHO} mb-3.5`}
                    onClick={() => { setMfaPending(false); setServerError(null); }}
                  >
                    {'‹ '}{t('login.back')}
                  </button>
                  <p className={KICK}>{t('login.codeKicker')}</p>
                  <h1 className="font-head text-[28px] font-extrabold leading-[1.1] m-0 mb-2">
                    {t('login.codeTitle')}
                  </h1>
                  <p className="text-[14.5px] text-nav-slate m-0 mb-[30px]">{t('login.codeSub')}</p>

                  <label className={FLABEL} htmlFor="lg-code">{t('login.mfaCode')}</label>
                  <input
                    id="lg-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder="000000"
                    className={`${INP} tabular-nums text-center text-[22px] tracking-[.35em] py-3.5 mb-3.5`}
                    {...register('mfaCode', {
                      required: t('login.codeRequired'),
                      pattern: { value: /^\d{6}$/, message: t('login.codeSixDigits') },
                    })}
                  />
                  {errors.mfaCode && (
                    <p className="text-[12.5px] text-nav-red m-0 mb-2.5" role="alert">{errors.mfaCode.message}</p>
                  )}
                  {serverError && (
                    <div className="flex gap-2.5 items-start bg-nav-redTint rounded-[9px] px-3.5 py-3 mb-3.5" role="alert">
                      <CircleAlert className="h-[18px] w-[18px] flex-none mt-0.5 text-nav-red" aria-hidden />
                      <p className="text-[13.5px] text-nav-red m-0">{serverError}</p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`${code.trim().length === 6 ? BTN_PRI : BTN_SEC} w-full py-3.5 text-base`}
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                    {t('login.verify')}
                  </button>
                </>
              )}
            </form>

            <p className="text-[12.5px] text-nav-slate m-0 mt-[26px]">{t('login.hipaa')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
